import { normalizeCidr, validateBlockPlacement, validateCidrInput, parseCidr } from './cidrUtils';
import type { CidrBlock, IpCountMode } from './types';

export type BulkCidrLine = {
  line: number;
  cidr: string;
  name: string;
};

export type BulkImportInvalidLine = {
  line: number;
  raw: string;
  error: string;
};

export type BulkImportValidLine = {
  line: number;
  cidr: string;
  name: string;
  normalized: string;
};

export type BulkImportPreview = {
  valid: BulkImportValidLine[];
  invalid: BulkImportInvalidLine[];
};

/**
 * 1 行分の CIDR と名前を解析する（空行・# コメントは null）
 */
export function parseBulkCidrLine(rawLine: string): { cidr: string; name: string } | null {
  const trimmed = rawLine.trim();
  if (!trimmed || trimmed.startsWith('#')) {
    return null;
  }

  const commaParts = trimmed.split(/[,\t]/);
  if (commaParts.length >= 2 && commaParts[0].trim().includes('/')) {
    return {
      cidr: commaParts[0].trim(),
      name: commaParts.slice(1).join(',').trim(),
    };
  }

  const spaceParts = trimmed.split(/\s+/);
  if (spaceParts.length >= 2 && spaceParts[0].includes('/')) {
    return {
      cidr: spaceParts[0],
      name: spaceParts.slice(1).join(' ').trim(),
    };
  }

  return { cidr: trimmed, name: '' };
}

function mapValidationError(error: string): string {
  if (error === 'OUT_OF_PARENT') return 'OUT_OF_PARENT';
  if (error === 'OVERLAP') return 'OVERLAP';
  if (error === 'AWS_TOO_SMALL') return 'AWS_TOO_SMALL';
  return 'INVALID';
}

/**
 * テキストから一括取り込み可能な CIDR を検証する
 */
export function previewBulkImport(
  text: string,
  parentCidr: string,
  existingCidrs: string[],
  ipCountMode: IpCountMode,
): BulkImportPreview {
  const valid: BulkImportValidLine[] = [];
  const invalid: BulkImportInvalidLine[] = [];
  const acceptedCidrs = [...existingCidrs];
  const acceptedNormalized = new Set(existingCidrs.map((cidr) => normalizeCidr(cidr)));

  const lines = text.split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index];
    const parsed = parseBulkCidrLine(raw);
    if (!parsed) {
      continue;
    }

    const line = index + 1;
    const inputValidation = validateCidrInput(parsed.cidr);
    if (!inputValidation.ok) {
      invalid.push({
        line,
        raw: raw.trim(),
        error: 'INVALID',
      });
      continue;
    }

    if (acceptedNormalized.has(inputValidation.normalized)) {
      invalid.push({
        line,
        raw: raw.trim(),
        error: 'DUPLICATE',
      });
      continue;
    }

    const validation = validateBlockPlacement(
      parsed.cidr,
      parentCidr,
      acceptedCidrs,
      undefined,
      ipCountMode,
    );

    if (!validation.ok) {
      invalid.push({
        line,
        raw: raw.trim(),
        error: mapValidationError(validation.error),
      });
      continue;
    }

    valid.push({
      line,
      cidr: parsed.cidr,
      name: parsed.name,
      normalized: inputValidation.normalized,
    });
    acceptedCidrs.push(inputValidation.normalized);
    acceptedNormalized.add(inputValidation.normalized);
  }

  return { valid, invalid };
}

function compareBlocksByNetwork(a: CidrBlock, b: CidrBlock): number {
  const aStart = parseCidr(a.cidr).network;
  const bStart = parseCidr(b.cidr).network;
  if (aStart < bStart) return -1;
  if (aStart > bStart) return 1;
  return 0;
}

/**
 * ブロック一覧を一括取り込み形式のテキストに変換する
 */
export function serializeBlocksToText(blocks: CidrBlock[]): string {
  return [...blocks]
    .sort(compareBlocksByNetwork)
    .map((block) => (block.name ? `${block.cidr}, ${block.name}` : block.cidr))
    .join('\n');
}
